import { useState } from 'react';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { useImportVoiceSamples, useVoiceSampleCount } from '../../hooks/useContent';
import type { VoicePersona } from '../../api/content';

const PERSONAS: Array<{ value: VoicePersona; label: string }> = [
  { value: 'patrick', label: 'Patrick (CEO)' },
  { value: 'evrard', label: 'Evrard (CTO)' },
  { value: 'sequesto', label: 'SEQUESTO' },
];

/**
 * Import a persona's real LinkedIn posts into their voice corpus. Each post
 * becomes an example the generator imitates, so the tone matches how the
 * person actually writes. Paste posts separated by a line containing only "---".
 */
export function VoiceSamplesModal({
  isOpen,
  onClose,
  initialPerson = 'evrard',
}: {
  isOpen: boolean;
  onClose: () => void;
  initialPerson?: VoicePersona;
}) {
  const [person, setPerson] = useState<VoicePersona>(initialPerson);
  const [text, setText] = useState('');
  const [imported, setImported] = useState<{ inserted: number; skipped: number } | null>(null);

  const importMutation = useImportVoiceSamples();
  const countQuery = useVoiceSampleCount(isOpen ? person : null);

  // Preview how many posts the "---" separator will produce.
  const detected = text
    .split(/^\s*---\s*$/m)
    .map((p) => p.trim())
    .filter(Boolean).length;

  function handleImport() {
    if (detected === 0) return;
    setImported(null);
    importMutation.mutate(
      { person, text },
      {
        onSuccess: (result) => {
          setImported(result);
          setText('');
        },
      },
    );
  }

  function handleClose() {
    setText('');
    setImported(null);
    importMutation.reset();
    onClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import published LinkedIn posts"
      description="Paste a person's real, already-posted content into the published library. Imported posts teach the generator their voice AND count as 'already went out' so it avoids repeating or cannibalising them."
      size="lg"
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="text-body-sm font-medium text-text-primary">Author</label>
          <select
            value={person}
            onChange={(e) => {
              setPerson(e.target.value as VoicePersona);
              setImported(null);
            }}
            className="w-full rounded-md border border-border bg-surface-primary px-3 py-1.5 text-body-sm text-text-primary"
          >
            {PERSONAS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          {typeof countQuery.data?.count === 'number' && (
            <p className="text-body-xs text-text-tertiary">
              {countQuery.data.count} post{countQuery.data.count === 1 ? '' : 's'} already in this author's library.
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label className="text-body-sm font-medium text-text-primary">Posts</label>
          <p className="text-body-xs text-text-tertiary">
            Paste one or more posts. Separate each post with a line containing only <code>---</code>.
          </p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={16}
            placeholder={'First real post text…\n\n---\n\nSecond real post text…'}
            className="w-full resize-y rounded-md border border-border bg-surface-primary px-3 py-2 text-body-sm text-text-primary focus:border-border-strong focus:outline-none"
          />
          <p className="text-body-xs text-text-tertiary">
            {detected} post{detected === 1 ? '' : 's'} detected.
          </p>
        </div>

        {imported !== null && (
          <Alert variant="success">
            Imported {imported.inserted} post{imported.inserted === 1 ? '' : 's'} into {PERSONAS.find((p) => p.value === person)?.label}'s published library
            {imported.skipped > 0 ? `, skipped ${imported.skipped} already in the library` : ''}.
          </Alert>
        )}
        {importMutation.error instanceof Error && (
          <Alert variant="error">{importMutation.error.message}</Alert>
        )}
      </div>

      <ModalFooter>
        <Button size="sm" variant="ghost" onClick={handleClose} disabled={importMutation.isPending}>
          Close
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={handleImport}
          isLoading={importMutation.isPending}
          disabled={detected === 0}
        >
          Import {detected > 0 ? `${detected} post${detected === 1 ? '' : 's'}` : ''}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
